import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { AppConstant } from '@d-constants/app.constants';

export interface AppConfig {
  inputStyle: string;
  colorScheme: string;
  theme: string;
  customTheme: string;
  ripple: boolean;
  menuMode: string;
  scale: number;
}

interface LayoutState {
  staticMenuDesktopInactive: boolean;
  overlayMenuActive: boolean;
  profileSidebarVisible: boolean;
  configSidebarVisible: boolean;
  staticMenuMobileActive: boolean;
  menuHoverActive: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  config: AppConfig = {
    ripple: false,
    inputStyle: 'outlined',
    menuMode: 'static',
    colorScheme: 'light',
    theme: 'lara-light-teal',
    customTheme: AppConstant.Style.CustomLightTheme,
    scale: 14,
  };

  state: LayoutState = {
    staticMenuDesktopInactive: false,
    overlayMenuActive: false,
    profileSidebarVisible: false,
    configSidebarVisible: false,
    staticMenuMobileActive: false,
    menuHoverActive: false,
  };

  private configUpdate = new Subject<AppConfig>();

  private overlayOpen = new Subject<any>();

  configUpdate$ = this.configUpdate.asObservable();

  overlayOpen$ = this.overlayOpen.asObservable();

  onMenuToggle() {
    if (this.isOverlay()) {
      this.state.overlayMenuActive = !this.state.overlayMenuActive;
      if (this.state.overlayMenuActive) {
        this.overlayOpen.next(null);
      }
    }

    if (this.isDesktop()) {
      this.state.staticMenuDesktopInactive =
        !this.state.staticMenuDesktopInactive;
    } else {
      this.state.staticMenuMobileActive = !this.state.staticMenuMobileActive;

      if (this.state.staticMenuMobileActive) {
        this.overlayOpen.next(null);
      }
    }
  }

  onOverlaySubmenuOpen() {
    this.overlayOpen.next(null);
  }

  showProfileSidebar() {
    this.state.profileSidebarVisible = !this.state.profileSidebarVisible;
    if (this.state.profileSidebarVisible) {
      this.overlayOpen.next(null);
    }
  }

  showConfigSidebar() {
    this.state.configSidebarVisible = true;
  }

  isOverlay() {
    return this.config.menuMode === 'overlay';
  }

  isDesktop() {
    return window.innerWidth > 991;
  }

  isMobile() {
    return !this.isDesktop();
  }

  onConfigUpdate() {
    this.configUpdate.next(this.config);
  }

  updateTheme(colorScheme: string) {
    const themeLink = <HTMLLinkElement>document.getElementById('app-theme');
    let theme = colorScheme === 'dark' ? 'lara-dark-teal' : 'lara-light-teal';
    localStorage.setItem('theme', colorScheme);

    const newHref = themeLink
      .getAttribute('href')!
      .replace(this.config.theme, theme);
    this.config.colorScheme;
    this.replaceThemeLink(newHref, () => {
      this.config.theme = theme;
      this.config.colorScheme = colorScheme;
      this.onConfigUpdate();
      this.changeCustomTheme();
    });
  }

  private changeCustomTheme() {
    var customThemeElement = document.getElementById('custom-theme');
    const themeLink = <HTMLLinkElement>customThemeElement;

    const customThemeLink =
      this.config.theme === 'lara-dark-teal'
        ? AppConstant.Style.CustomDarkTheme
        : AppConstant.Style.CustomLightTheme;
    const newHref = themeLink
      .getAttribute('href')!
      .replace(this.config.customTheme, customThemeLink);

    customThemeElement?.setAttribute('href', newHref);
    this.config.customTheme = newHref;
    this.onConfigUpdate();
  }

  private replaceThemeLink(href: string, onComplete: Function) {
    const id = 'app-theme';
    const themeLink = <HTMLLinkElement>document.getElementById(id);
    const cloneLinkElement = <HTMLLinkElement>themeLink.cloneNode(true);

    cloneLinkElement.setAttribute('href', href);
    cloneLinkElement.setAttribute('id', id + '-clone');

    themeLink.parentNode!.insertBefore(cloneLinkElement, themeLink.nextSibling);

    cloneLinkElement.addEventListener('load', () => {
      themeLink.remove();
      cloneLinkElement.setAttribute('id', id);
      onComplete();
    });
  }
}
